import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import ClaraVideoModal from "../components/ClaraVideoModal";
import ClaraChatModal from "../components/ClaraChatModal";

import ClaraChatRoom from "../components/ClaraChatRoom";
import ClaraCalendar from "../components/calendar/ClaraCalendar";
import ClaraMailAI from "../components/ClaraMailAI";
import EmailSummaryModal from "../components/EmailSummaryModal";
import { 
  MessageSquare, 
  Mail, 
  MailOpen,
  Calendar, 
  FileText, 
  Megaphone,
  Video,
  Plus,
  Edit,
  Trash2,
  Eye,
  AlertCircle,
  Info,
  Wrench,
  Calendar as CalendarIcon,
  Send,
  Phone,
  User,
  Clock,
  CheckCircle,
  XCircle,
  Loader,
  RefreshCw,
  Bell,
  ExternalLink,
  Users,
  Sparkles,
  ChevronDown,
  Crown,
  Search,
  Filter,
  Download,
  Upload,
  EyeOff,
  X,
  Share2,
  Copy,
  Monitor,
  Mic,
  MicOff,
  Camera,
  CameraOff,
  ScreenShare,
  ScreenShareOff,
  Volume2,
  VolumeX,
  Shield,
  Server,
  Database,
  Activity,
  UserPlus,
  UserMinus,
  Key,
  Lock,
  Unlock,
  Power,
  PowerOff,
  RotateCcw,
  Terminal,
  Globe,
  Wifi,
  WifiOff,
  Navigation,
  Home,
  Archive,
  Brain,
  Settings,
  Check
} from 'lucide-react';

const CommunicationPage = () => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('clara-video');
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [showTelegramConnect, setShowTelegramConnect] = useState(false);
  const [telegramConnected, setTelegramConnected] = useState(false);
  const [telegramContacts, setTelegramContacts] = useState([]);
  const [telegramQrCode, setTelegramQrCode] = useState(null);
  const [telegramLoginToken, setTelegramLoginToken] = useState(null);
  const [telegramStatus, setTelegramStatus] = useState('idle'); // idle, qr_pending, phone_pending, connected
  const [telegramPhoneHash, setTelegramPhoneHash] = useState(null);
  const [telegramUser, setTelegramUser] = useState(null);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [useIframe, setUseIframe] = useState(true);
  
  // Telegram Chat States
  const [showTelegramChat, setShowTelegramChat] = useState(false);
  const [telegramChats, setTelegramChats] = useState([]);
  const [selectedTelegramChat, setSelectedTelegramChat] = useState(null);
  const [telegramMessages, setTelegramMessages] = useState({});  // Changed: Object with chat_id as key
  const [newTelegramMessage, setNewTelegramMessage] = useState('');
  const [telegramChatLoading, setTelegramChatLoading] = useState(false);
  
  // Mail Intelligence State
  const [emails, setEmails] = useState([]);
  const [emailsLoading, setEmailsLoading] = useState(false);
  const [emailsError, setEmailsError] = useState(null);
  const [showEmailSummary, setShowEmailSummary] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);

  // Telegram API Base URL
  const TELEGRAM_API_BASE = 'https://clara360.de/api/telegram';

  // Telegram Connect Handler - THE MISSING FUNCTION!
  const handleTelegramConnect = async (method) => {
    try {
      if (method === 'qr') {
        setTelegramStatus('qr_pending');
        
        // Request QR code from backend
        const response = await fetch(`${TELEGRAM_API_BASE}/connect-qr`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          }
        });
        
        const data = await response.json();
        
        if (data.success) {
          setTelegramQrCode(data.qr_code);
          setTelegramLoginToken(data.login_token);
          
          // Start polling for QR scan status
          pollQrStatus(data.login_token);
        } else {
          alert('Fehler beim Generieren des QR-Codes: ' + data.error);
          setTelegramStatus('idle');
        }
        
      } else if (method === 'phone') {
        const phone = prompt('Bitte geben Sie Ihre Telefonnummer ein (mit Ländercode, z.B. +49123456789):');
        if (!phone) return;
        
        setTelegramStatus('phone_pending');
        
        const response = await fetch(`${TELEGRAM_API_BASE}/connect-phone`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ phone })
        });
        
        const data = await response.json();
        
        if (data.success) {
          setTelegramPhoneHash(data.phone_hash);
          
          // Ask for verification code
          const code = prompt('Bitte geben Sie den Bestätigungscode ein, den Sie per SMS erhalten haben:');
          if (code) {
            await verifyTelegramCode(data.phone_hash, code);
          }
        } else {
          alert('Fehler beim Senden des Codes: ' + data.error);
          setTelegramStatus('idle');
        }
      }
    } catch (error) {
      console.error('Telegram connection error:', error);
      alert('Verbindungsfehler: ' + error.message);
      setTelegramStatus('idle');
    }
  };

  // Poll QR code status
  const pollQrStatus = async (loginToken) => {
    const maxAttempts = 60; // 5 minutes with 5-second intervals
    let attempts = 0;
    
    const poll = async () => {
      try {
        const response = await fetch(`${TELEGRAM_API_BASE}/check-qr-status/${loginToken}`);
        const data = await response.json();
        
        if (data.success) {
          if (data.status === 'completed') {
            setTelegramUser(data.user);
            setTelegramConnected(true);
            setTelegramStatus('connected');
            setShowTelegramConnect(false);
            alert(`Erfolgreich verbunden als ${data.user.first_name}!`);
            return;
          } else if (data.status === 'pending') {
            attempts++;
            if (attempts < maxAttempts) {
              setTimeout(poll, 5000); // Poll every 5 seconds
            } else {
              alert('QR-Code ist abgelaufen. Bitte versuchen Sie es erneut.');
              setTelegramStatus('idle');
              setTelegramQrCode(null);
            }
          }
        }
      } catch (error) {
        console.error('QR polling error:', error);
        setTelegramStatus('idle');
      }
    };
    
    poll();
  };

  // Verify Telegram code
  const verifyTelegramCode = async (phoneHash, code, password = null) => {
    try {
      const response = await fetch(`${TELEGRAM_API_BASE}/verify-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          phone_hash: phoneHash, 
          code,
          password 
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setTelegramUser(data.user);
        setTelegramConnected(true);
        setTelegramStatus('connected');
        setShowTelegramConnect(false);
        alert(`Erfolgreich verbunden als ${data.user.first_name}!`);
      } else if (data.error === '2FA_REQUIRED') {
        const password = prompt('Zwei-Faktor-Authentifizierung erforderlich. Bitte geben Sie Ihr Passwort ein:');
        if (password) {
          await verifyTelegramCode(phoneHash, code, password);
        }
      } else {
        alert('Fehler bei der Verifizierung: ' + data.message);
        setTelegramStatus('idle');
      }
    } catch (error) {
      console.error('Code verification error:', error);
      alert('Verifizierungsfehler: ' + error.message);
      setTelegramStatus('idle');
    }
  };

  // Check Telegram status on component mount
  useEffect(() => {
    const checkTelegramStatus = async () => {
      try {
        const response = await fetch(`${TELEGRAM_API_BASE}/status`);
        const data = await response.json();
        
        if (data.connected && data.users && data.users.length > 0) {
          setTelegramConnected(true);
          setTelegramUser(data.users[0]); // Use first user for now
          setTelegramStatus('connected');
        }
      } catch (error) {
        console.error('Error checking Telegram status:', error);
      }
    };
    
    checkTelegramStatus();
  }, []);

  // Fetch Telegram chats
  const fetchTelegramChats = async () => {
    if (!telegramUser) {
      console.error('No Telegram user found');
      return;
    }
    
    setTelegramChatLoading(true);
    try {
      const response = await fetch(`${TELEGRAM_API_BASE}/chats/${telegramUser.id}`);
      const data = await response.json();
      
      if (data.success) {
        setTelegramChats(data.chats);
        console.log(`Loaded ${data.chats.length} Telegram chats`);
      } else {
        console.error('Failed to fetch chats:', data.error);
      }
    } catch (error) {
      console.error('Error fetching Telegram chats:', error);
    } finally {
      setTelegramChatLoading(false);
    }
  };

  // Send Telegram message with improved error handling
  const sendTelegramMessage = async () => {
    if (!newTelegramMessage.trim() || !selectedTelegramChat || !telegramUser) {
      return;
    }

    const messageText = newTelegramMessage.trim();
    const tempMessageId = Date.now(); // Unique ID for temp message
    
    const tempMessage = {
      id: tempMessageId,
      content: messageText,
      is_from_me: true,
      sender_name: telegramUser.first_name,
      timestamp: new Date().toISOString(),
      status: 'sending' // Add status for UI feedback
    };

    // Add message to UI immediately (chat-specific)
    const chatId = selectedTelegramChat.id;
    setTelegramMessages(prev => ({
      ...prev,
      [chatId]: [...(prev[chatId] || []), tempMessage]
    }));
    setNewTelegramMessage('');

    try {
      const response = await fetch(`${TELEGRAM_API_BASE}/send-message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: telegramUser.id,
          chat_id: selectedTelegramChat.id,
          message: messageText
        })
      });

      const data = await response.json();
      
      if (data.success) {
        // Update temp message to success (chat-specific)
        setTelegramMessages(prev => ({
          ...prev,
          [chatId]: (prev[chatId] || []).map(msg => 
            msg.id === tempMessageId 
              ? { ...msg, status: 'sent', id: data.message_id || tempMessageId }
              : msg
          )
        }));
        console.log('Message sent successfully');
      } else {
        // Handle different error types with user-friendly messages
        let errorMessage = 'Nachricht konnte nicht gesendet werden';
        
        switch (data.code) {
          case 'WRITE_FORBIDDEN':
            errorMessage = 'Keine Berechtigung in diesem Chat zu schreiben';
            break;
          case 'USER_BANNED':
            errorMessage = 'Sie sind in diesem Chat gesperrt';
            break;
          case 'CHANNEL_PRIVATE':
            errorMessage = 'Dieser Channel ist privat';
            break;
          case 'BROADCAST_CHANNEL':
            errorMessage = 'In Broadcast-Channels können keine Nachrichten gesendet werden';
            break;
          case 'NO_CHANNEL_PERMISSION':
            errorMessage = 'Keine Berechtigung in diesem Channel';
            break;
          case 'CHAT_NOT_ACCESSIBLE':
            errorMessage = 'Chat ist nicht zugänglich';
            break;
          case 'INVALID_CHAT_ID':
          case 'INVALID_PEER':
            errorMessage = 'Ungültige Chat-ID';
            break;
          default:
            errorMessage = data.error || 'Unbekannter Fehler';
        }

        console.error('Failed to send message:', data.error);
        
            // Update temp message to error (chat-specific)
        setTelegramMessages(prev => ({
          ...prev,
          [chatId]: (prev[chatId] || []).map(msg => 
            msg.id === tempMessageId 
              ? { ...msg, status: 'error', error: errorMessage }
              : msg
          )
        }));

        // Show error notification
        alert(`Fehler beim Senden: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Error sending Telegram message:', error);
      
      // Update temp message to error state
      setTelegramMessages(prev => ({
        ...prev,
        [chatId]: (prev[chatId] || []).map(msg => 
          msg.id === tempMessageId 
            ? { ...msg, status: 'error', error: 'Netzwerkfehler' }
            : msg
        )
      }));

      // Show error notification
      alert('Netzwerkfehler beim Senden der Nachricht');
    }
  };

  // Retry failed message
  const retryMessage = async (messageId) => {
    if (!selectedTelegramChat) return;
    
    const chatId = selectedTelegramChat.id;
    const message = telegramMessages[chatId]?.find(msg => msg.id === messageId);
    if (!message) return;

    // Update status to sending (chat-specific)
    setTelegramMessages(prev => ({
      ...prev,
      [chatId]: (prev[chatId] || []).map(msg => 
        msg.id === messageId 
          ? { ...msg, status: 'sending', error: null }
          : msg
      )
    }));

    try {
      const response = await fetch(`${TELEGRAM_API_BASE}/send-message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: telegramUser.id,
          chat_id: selectedTelegramChat.id,
          message: message.content
        })
      });

      const data = await response.json();
      
      if (data.success) {
        // Update to success (chat-specific)
        setTelegramMessages(prev => ({
          ...prev,
          [chatId]: (prev[chatId] || []).map(msg => 
            msg.id === messageId 
              ? { ...msg, status: 'sent', id: data.message_id || messageId }
              : msg
          )
        }));
      } else {
        // Update to error (chat-specific)
        setTelegramMessages(prev => ({
          ...prev,
          [chatId]: (prev[chatId] || []).map(msg => 
            msg.id === messageId 
              ? { ...msg, status: 'error', error: data.error || 'Retry failed' }
              : msg
          )
        }));
      }
    } catch (error) {
      // Update to network error (chat-specific)
      setTelegramMessages(prev => ({
        ...prev,
        [chatId]: (prev[chatId] || []).map(msg => 
          msg.id === messageId 
            ? { ...msg, status: 'error', error: 'Netzwerkfehler' }
            : msg
        )
      }));
    }
  };

  // Function to load messages for a specific chat
  const loadChatMessages = async (chatId) => {
    if (!telegramUser || !chatId) return;
    
    try {
      const response = await fetch(`${TELEGRAM_API_BASE}/messages/${chatId}?user_id=${telegramUser.id}&limit=50&days_back=60`);
      const data = await response.json();
      
      if (data.success) {
        setTelegramMessages(prev => ({
          ...prev,
          [chatId]: data.messages || []
        }));
      }
    } catch (error) {
      console.error('Error loading chat messages:', error);
    }
  };

  // Bulk import function for 2M Import button
  const handleBulkImport = async (days) => {
    if (!telegramUser) return;
    
    try {
      const response = await fetch('/api/telegram/chats/bulk-import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: telegramUser.id,
          days_back: days
        })
      });
      
      const data = await response.json();
      if (data.success) {
        alert(`Import erfolgreich: ${data.imported_messages} Nachrichten aus ${data.processed_chats} Chats importiert`);
        // Reload chats and current chat messages
        loadTelegramChats();
        if (selectedTelegramChat) {
          loadChatMessages(selectedTelegramChat.id);
        }
      } else {
        alert(`Import fehlgeschlagen: ${data.error}`);
      }
    } catch (error) {
      alert(`Import Fehler: ${error.message}`);
    }
  };

  // Toggle favorite function
  const handleToggleFavorite = async (chatId, isFavorite) => {
    if (!telegramUser) return;
    
    try {
      const response = await fetch('/api/telegram/chat/favorite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: telegramUser.id,
          chat_id: chatId,
          is_favorite: isFavorite
        })
      });
      
      const data = await response.json();
      if (data.success) {
        // Update chat list
        setTelegramChats(prev => prev.map(chat => 
          chat.id === chatId ? { ...chat, is_favorite: isFavorite } : chat
        ));
        // Update selected chat
        if (selectedTelegramChat && selectedTelegramChat.id === chatId) {
          setSelectedTelegramChat(prev => ({ ...prev, is_favorite: isFavorite }));
        }
      } else {
        alert(`Favorit Fehler: ${data.error}`);
      }
    } catch (error) {
      alert(`Favorit Fehler: ${error.message}`);
    }
  };

  // Delete chat function
  const handleDeleteChat = async (chatId) => {
    if (!telegramUser) return;
    
    if (!confirm('Chat wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.')) {
      return;
    }
    
    try {
      const response = await fetch('/api/telegram/chat/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: telegramUser.id,
          chat_id: chatId
        })
      });
      
      const data = await response.json();
      if (data.success) {
        // Remove from chat list
        setTelegramChats(prev => prev.filter(chat => chat.id !== chatId));
        // Clear selection if deleted chat was selected
        if (selectedTelegramChat && selectedTelegramChat.id === chatId) {
          setSelectedTelegramChat(null);
          setTelegramMessages(prev => {
            const newMessages = { ...prev };
            delete newMessages[chatId];
            return newMessages;
          });
        }
        alert('Chat erfolgreich gelöscht');
      } else {
        alert(`Löschen fehlgeschlagen: ${data.error}`);
      }
    } catch (error) {
      alert(`Löschen Fehler: ${error.message}`);
    }
  };

  // Render message content with clickable links
  const renderMessageContent = (content) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = content.split(urlRegex);
    
    return parts.map((part, index) => {
      if (urlRegex.test(part)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 hover:text-blue-700 underline"
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  // Load messages when selecting a chat
  useEffect(() => {
    if (selectedTelegramChat && telegramUser) {
      const chatId = selectedTelegramChat.id;
      // Only load if we don't have messages for this chat yet
      if (!telegramMessages[chatId]) {
        loadChatMessages(chatId);
      }
    }
  }, [selectedTelegramChat, telegramUser, telegramMessages]);

  // Auto-load chats when Telegram chat modal opens
  useEffect(() => {
    if (showTelegramChat && telegramConnected && telegramUser && telegramChats.length === 0) {
      fetchTelegramChats();
    }
  }, [showTelegramChat, telegramConnected, telegramUser]);

  // Fetch emails from ClaraMailBridge
  const fetchEmails = async () => {
    setEmailsLoading(true);
    setEmailsError(null);
    try {
      const response = await fetch('https://clara360.de/api/mail/latest?limit=3');
      if (!response.ok) throw new Error('Failed to fetch emails');
      const data = await response.json();
      setEmails(data.emails || []);
    } catch (error) {
      console.error('Error fetching emails:', error);
      setEmailsError(error.message);
    } finally {
      setEmailsLoading(false);
    }
  };

  // Handle email summarization
  const handleEmailSummarize = (email) => {
    setSelectedEmail(email);
    setShowEmailSummary(true);
  };

  // Load emails on component mount
  useEffect(() => {
    fetchEmails();
  }, []);
  const [selectedChatRoom, setSelectedChatRoom] = useState(null);
  const [whatsappContacts, setWhatsappContacts] = useState([]);
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  const [webhookStatus, setWebhookStatus] = useState({ connected: false });
  
  // Admin Panel State
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [adminLoading, setAdminLoading] = useState(false);
  const [matrixServerStatus, setMatrixServerStatus] = useState({
    online: false,
    users: 0,
    rooms: 0,
    uptime: 'Unknown',
    version: 'Unknown',
    federation: false
  });
  const [matrixUsers, setMatrixUsers] = useState([]);
  const [whatsappBridgeStatus, setWhatsappBridgeStatus] = useState({
    connected: false,
    privateAccount: { connected: false, phone: 'Not configured' },
    businessAccount: { connected: false, phone: 'Not configured' }
  });
  
  // New Messaging Interface State
  const [selectedMessagingService, setSelectedMessagingService] = useState('whatsapp-private');
  const [selectedContact, setSelectedContact] = useState(null);
  const [message, setMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);

  // Matrix API Base URL - Using chat.clara360.de for Matrix Admin API
  const MATRIX_API_BASE = 'https://chat.clara360.de/api/matrix';

  // Sample data for non-admin features
  const upcomingMeetings = [
    {
      id: 1,
      name: "Team Meeting",
      time: "14:00",
      date: new Date(),
      participants: 5,
      roomId: "team-meeting-123"
    },
    {
      id: 2,
      name: "Client Call",
      time: "16:30",
      date: new Date(),
      participants: 3,
      roomId: "client-call-456"
    }
  ];

  const recentMeetings = [
    {
      id: 1,
      name: "Daily Standup",
      date: new Date(Date.now() - 86400000),
      duration: "30 min",
      participants: 8,
      roomId: "daily-standup-789"
    },
    {
      id: 2,
      name: "Project Review",
      date: new Date(Date.now() - 172800000),
      duration: "1h 15min",
      participants: 6,
      roomId: "project-review-101"
    }
  ];

  const chatRooms = [
    {
      id: 1,
      name: "Allgemein",
      lastMessage: "Hallo zusammen! Wie läuft das Projekt?",
      lastActivity: new Date(Date.now() - 300000),
      members: 12,
      unread: 3,
      encrypted: true
    },
    {
      id: 2,
      name: "Entwicklung",
      lastMessage: "Der neue Build ist fertig zum Testen",
      lastActivity: new Date(Date.now() - 600000),
      members: 8,
      unread: 0,
      encrypted: true
    },
    {
      id: 3,
      name: "Support",
      lastMessage: "Ticket #1234 wurde geschlossen",
      lastActivity: new Date(Date.now() - 1800000),
      members: 5,
      unread: 1,
      encrypted: true
    }
  ];

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  // Matrix API Functions
  const fetchMatrixStatus = async () => {
    try {
      const response = await fetch(`${MATRIX_API_BASE}/status`);
      if (response.ok) {
        const data = await response.json();
        setMatrixServerStatus(data);
      }
    } catch (error) {
      console.error('Error fetching Matrix status:', error);
    }
  };

  const fetchMatrixUsers = async () => {
    try {
      const response = await fetch(`${MATRIX_API_BASE}/users`);
      if (response.ok) {
        const data = await response.json();
        setMatrixUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error fetching Matrix users:', error);
    }
  };

  const fetchWhatsAppStatus = async () => {
    try {
      const response = await fetch(`${MATRIX_API_BASE}/whatsapp/status`);
      if (response.ok) {
        const data = await response.json();
        setWhatsappBridgeStatus(data);
      }
    } catch (error) {
      console.error('Error fetching WhatsApp status:', error);
    }
  };

  // Load initial data when admin panel is shown
  useEffect(() => {
    if (showAdminPanel) {
      fetchMatrixStatus();
      fetchMatrixUsers();
      fetchWhatsAppStatus();
    }
  }, [showAdminPanel]);

  const formatDate = (date) => {
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const joinMeeting = (meeting) => {
    setSelectedMeeting(meeting);
    setShowVideoModal(true);
  };

  const copyMeetingLink = (roomId) => {
    const link = `https://meet.clara360.de/${roomId}`;
    navigator.clipboard.writeText(link);
    // You could add a toast notification here
  };

  const openChatRoom = (room) => {
    setSelectedChatRoom(room);
  };

  const loadWhatsAppContacts = async () => {
    setWhatsappLoading(true);
    // Simulate API call
    setTimeout(() => {
      setWhatsappContacts([
        {
          id: 1,
          name: "Max Mustermann",
          phone: "+49 123 456 7890",
          lastMessage: "Danke für die schnelle Antwort!",
          lastActivity: new Date(Date.now() - 300000),
          unread: 2
        },
        {
          id: 2,
          name: "Anna Schmidt",
          phone: "+49 987 654 3210",
          lastMessage: "Können wir morgen telefonieren?",
          lastActivity: new Date(Date.now() - 600000),
          unread: 0
        }
      ]);
      setWhatsappLoading(false);
    }, 1000);
  };

  // Admin Panel Functions
  const toggleAdminPanel = () => {
    setShowAdminPanel(!showAdminPanel);
  };

  const refreshMatrixStatus = async () => {
    setAdminLoading(true);
    try {
      await fetchMatrixStatus();
      await fetchMatrixUsers();
    } catch (error) {
      console.error('Error refreshing Matrix status:', error);
    } finally {
      setAdminLoading(false);
    }
  };

  const createMatrixUser = async () => {
    // This would show a modal for user creation
    const userId = prompt('Enter user ID (e.g., @user:clara360.de):');
    const password = prompt('Enter password:');
    
    if (userId && password) {
      try {
        const response = await fetch(`${MATRIX_API_BASE}/users`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            user_id: userId,
            password: password,
            admin: false
          })
        });
        
        if (response.ok) {
          alert('User created successfully!');
          fetchMatrixUsers(); // Refresh user list
        } else {
          alert('Failed to create user');
        }
      } catch (error) {
        console.error('Error creating user:', error);
        alert('Error creating user');
      }
    }
  };

  const deleteMatrixUser = async (userId) => {
    if (confirm(`Are you sure you want to delete user ${userId}?`)) {
      try {
        const response = await fetch(`${MATRIX_API_BASE}/users/${encodeURIComponent(userId)}`, {
          method: 'DELETE'
        });
        
        if (response.ok) {
          alert('User deleted successfully!');
          fetchMatrixUsers(); // Refresh user list
        } else {
          alert('Failed to delete user');
        }
      } catch (error) {
        console.error('Error deleting user:', error);
        alert('Error deleting user');
      }
    }
  };

  const restartWhatsAppBridge = async () => {
    setAdminLoading(true);
    try {
      const response = await fetch(`${MATRIX_API_BASE}/whatsapp/restart`, {
        method: 'POST'
      });
      
      if (response.ok) {
        alert('WhatsApp bridge restart initiated');
        // Refresh status after a delay
        setTimeout(() => {
          fetchWhatsAppStatus();
        }, 2000);
      } else {
        alert('Failed to restart WhatsApp bridge');
      }
    } catch (error) {
      console.error('Error restarting WhatsApp bridge:', error);
      alert('Error restarting WhatsApp bridge');
    } finally {
      setAdminLoading(false);
    }
  };

  // Send Message Function for WhatsApp Integration
  const sendMessage = async () => {
    if (!message.trim() || !selectedContact) return;

    try {
      const response = await fetch(`${MATRIX_API_BASE}/whatsapp/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          account_type: selectedMessagingService.replace('whatsapp-', ''),
          recipient: selectedContact.phone,
          message: message
        })
      });

      if (response.ok) {
        setChatHistory(prev => [...prev, {
          type: 'sent',
          message: message,
          timestamp: new Date().toLocaleTimeString()
        }]);
        setMessage('');
      } else {
        console.error('Failed to send message');
        alert('Fehler beim Senden der Nachricht');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Fehler beim Senden der Nachricht');
    }
  };

  const renderClaraVideoContent = () => {
    return (
      <div className="space-y-6">
        {/* Quick Start */}
        <Card className="dark:bg-slate-900 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 dark:text-white">
              <Video className="w-5 h-5 text-blue-600" />
              Schnellstart
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={() => setShowVideoModal(true)}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                <Video className="w-4 h-4 mr-2" />
                Sofort-Meeting starten
              </Button>
              <Button 
                variant="outline" 
                className="flex-1 dark:border-gray-600 dark:text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Meeting planen
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Upcoming Meetings */}
        <Card className="dark:bg-slate-900 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 dark:text-white">
              <Calendar className="w-5 h-5 text-blue-600" />
              Anstehende Meetings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {upcomingMeetings.map((meeting) => (
                <div key={meeting.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-slate-800 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                      <Video className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium dark:text-white">{meeting.name}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        {formatDate(meeting.date)} • {meeting.time} • {meeting.participants} Teilnehmer
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyMeetingLink(meeting.roomId)}
                      className="dark:border-gray-600 dark:text-white"
                    >
                      <Share2 className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => joinMeeting(meeting)}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      Beitreten
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recent Meetings */}
        <Card className="dark:bg-slate-900 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 dark:text-white">
              <Clock className="w-5 h-5 text-green-600" />
              Letzte Meetings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentMeetings.map((meeting) => (
                <div key={meeting.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-slate-800 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
                      <Clock className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium dark:text-white">{meeting.name}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        {formatDate(meeting.date)} • {meeting.duration} • {meeting.participants} Teilnehmer
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyMeetingLink(meeting.roomId)}
                      className="dark:border-gray-600 dark:text-white"
                    >
                      <Share2 className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => joinMeeting(meeting)}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      Beitreten
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderClaraChatContent = () => {
    return (
      <div className="space-y-6">
        
        {/* 1. QUICK START SECTION */}
        <div className="bg-white dark:bg-slate-900 rounded-lg shadow-md p-6">
          <div className="flex items-center mb-4">
            <MessageSquare className="w-6 h-6 text-blue-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Schnell-Einstieg
            </h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button 
              onClick={() => openChatRoom(chatRooms[0])}
              className="flex items-center justify-center p-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors h-auto"
            >
              <Users className="w-5 h-5 mr-2" />
              Chatraum beitreten
            </Button>
            
            <Button 
              onClick={() => setShowChatModal(true)}
              className="flex items-center justify-center p-4 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors h-auto"
            >
              <Plus className="w-5 h-5 mr-2" />
              Neuen Raum erstellen
            </Button>
            
            <Button 
              onClick={() => window.open('https://chat.clara360.de', '_blank')}
              className="flex items-center justify-center p-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors h-auto"
            >
              <ExternalLink className="w-5 h-5 mr-2" />
              Element Web öffnen
            </Button>
          </div>
        </div>

        {/* 2. TELEGRAM INTEGRATION SECTION */}
        <Card className="dark:bg-slate-900 dark:border-gray-700 border-2 border-blue-200 dark:border-blue-800">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 dark:text-white">
                <MessageSquare className="w-6 h-6 text-blue-600 mr-2" />
                Telegram Integration
                <Badge className={`text-xs rounded-full ml-2 ${
                  telegramConnected ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {telegramConnected ? 'Verbunden' : 'Nicht verbunden'}
                </Badge>
              </CardTitle>
              {telegramConnected && telegramUser && (
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <User className="w-4 h-4" />
                  {telegramUser.first_name} {telegramUser.last_name}
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {!telegramConnected ? (
              <div className="space-y-4">
                <p className="text-gray-600 dark:text-gray-400">
                  Verbinden Sie Ihr Telegram-Konto, um Nachrichten direkt über Clara360 zu senden und zu empfangen.
                </p>
                <div className="flex gap-3">
                  <Button
                    onClick={() => setShowTelegramConnect(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    disabled={telegramStatus !== 'idle'}
                  >
                    {telegramStatus === 'idle' ? (
                      <>
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Telegram verbinden
                      </>
                    ) : (
                      <>
                        <Loader className="w-4 h-4 mr-2 animate-spin" />
                        Verbinde...
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-green-50 dark:bg-green-900 p-4 rounded-lg">
                  <div className="flex items-center gap-2 text-green-800 dark:text-green-200">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">Telegram erfolgreich verbunden!</span>
                  </div>
                  <p className="text-green-700 dark:text-green-300 text-sm mt-1">
                    Sie können jetzt Telegram-Nachrichten über Clara360 verwalten.
                  </p>
                </div>
                <div className="flex flex-wrap gap-3">
                  <Button
                    onClick={() => setShowTelegramChat(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Chats öffnen
                  </Button>
                  
                  {/* Chat Management Buttons */}
                  <Button
                    onClick={() => {
                      // Real bulk import implementation
                      handleBulkImport(60);
                    }}
                    variant="outline"
                    className="border-blue-300 text-blue-600 hover:bg-blue-50"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    2 Monate importieren
                  </Button>
                  
                  <Button
                    onClick={() => {
                      setTelegramConnected(false);
                      setTelegramUser(null);
                      setTelegramStatus('idle');
                    }}
                    variant="outline"
                    className="border-red-300 text-red-600 hover:bg-red-50"
                  >
                    Trennen
                  </Button>
                </div>
                
                {/* Chat Management Stats */}
                <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900 rounded-lg">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-blue-800 dark:text-blue-200">
                      <Users className="w-4 h-4 inline mr-1" />
                      {telegramContacts.length} Chats verfügbar
                    </span>
                    <span className="text-blue-600 dark:text-blue-300">
                      <Clock className="w-4 h-4 inline mr-1" />
                      Letzte Sync: vor 2 Min
                    </span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 3. QUICK NAVIGATION SECTION */}
        <div className="bg-white dark:bg-slate-900 rounded-lg shadow-md p-6">
          <div className="flex items-center mb-4">
            <Navigation className="w-6 h-6 text-indigo-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Schnell-Navigation
            </h3>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Button 
              variant="outline"
              onClick={() => setActiveTab('clara-video')}
              className="flex items-center justify-center p-3 bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 rounded-lg hover:bg-indigo-200 dark:hover:bg-indigo-800 transition-colors border-indigo-200 dark:border-indigo-700"
            >
              <Home className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
            
            <Button 
              variant="outline"
              onClick={() => setShowAdminPanel(!showAdminPanel)}
              className="flex items-center justify-center p-3 bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 rounded-lg hover:bg-indigo-200 dark:hover:bg-indigo-800 transition-colors border-indigo-200 dark:border-indigo-700"
            >
              <Settings className="w-4 h-4 mr-2" />
              Einstellungen
            </Button>
            
            <Button 
              variant="outline"
              onClick={() => setActiveTab('whatsapp')}
              className="flex items-center justify-center p-3 bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 rounded-lg hover:bg-indigo-200 dark:hover:bg-indigo-800 transition-colors border-indigo-200 dark:border-indigo-700"
            >
              <Users className="w-4 h-4 mr-2" />
              Kontakte
            </Button>
            
            <Button 
              variant="outline"
              className="flex items-center justify-center p-3 bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 rounded-lg hover:bg-indigo-200 dark:hover:bg-indigo-800 transition-colors border-indigo-200 dark:border-indigo-700"
            >
              <Archive className="w-4 h-4 mr-2" />
              Archiv
            </Button>
          </div>
        </div>

        {/* 4. ADMIN PANEL SECTION (INLINE) */}
        <Card className="dark:bg-slate-900 dark:border-gray-700 border-2 border-orange-200 dark:border-orange-800">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 dark:text-white">
                <Crown className="w-6 h-6 text-red-600 mr-2" />
                Matrix Admin Panel
                <Badge className="bg-green-100 text-green-800 text-xs rounded-full ml-2">
                  Live API
                </Badge>
              </CardTitle>
              <Button
                onClick={toggleAdminPanel}
                variant="outline"
                size="sm"
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 dark:border-gray-600"
              >
                {showAdminPanel ? 'Ausblenden' : 'Anzeigen'}
              </Button>
            </div>
          </CardHeader>

          {showAdminPanel && (
            <CardContent className="space-y-6 border-t border-gray-200 dark:border-gray-700 pt-6">
              {/* Server Status */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-green-50 dark:bg-green-900 p-4 rounded-lg">
                  <div className="flex items-center">
                    <Server className="w-5 h-5 text-green-600 mr-2" />
                    <h4 className="font-semibold text-gray-900 dark:text-white">Server Status</h4>
                  </div>
                  <div className="mt-2">
                    <div className="flex items-center">
                      <div className={`w-2 h-2 rounded-full mr-2 ${matrixServerStatus.online ? 'bg-green-500' : 'bg-red-500'}`}></div>
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {matrixServerStatus.online ? 'Online' : 'Offline'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Version: {matrixServerStatus.version}</p>
                  </div>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900 p-4 rounded-lg">
                  <div className="flex items-center">
                    <Users className="w-5 h-5 text-blue-600 mr-2" />
                    <h4 className="font-semibold text-gray-900 dark:text-white">Benutzer</h4>
                  </div>
                  <div className="mt-2">
                    <span className="text-2xl font-bold text-blue-600">{matrixServerStatus.users}</span>
                    <p className="text-xs text-gray-500">Registrierte Benutzer</p>
                  </div>
                </div>

                <div className="bg-purple-50 dark:bg-purple-900 p-4 rounded-lg">
                  <div className="flex items-center">
                    <MessageSquare className="w-5 h-5 text-purple-600 mr-2" />
                    <h4 className="font-semibold text-gray-900 dark:text-white">Räume</h4>
                  </div>
                  <div className="mt-2">
                    <span className="text-2xl font-bold text-purple-600">{matrixServerStatus.rooms}</span>
                    <p className="text-xs text-gray-500">Aktive Räume</p>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={refreshMatrixStatus}
                  disabled={adminLoading}
                  size="sm"
                  className="bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {adminLoading ? <Loader className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Status aktualisieren
                </Button>
                <Button
                  onClick={createMatrixUser}
                  size="sm"
                  className="bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Benutzer erstellen
                </Button>
                <Button
                  onClick={() => window.open('https://matrix.clara360.de/_synapse/admin', '_blank')}
                  size="sm"
                  className="bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <Terminal className="w-4 h-4 mr-2" />
                  Synapse Admin
                </Button>
              </div>
            </CardContent>
          )}
        </Card>

        {/* 5. MESSAGING SECTION (ALWAYS OPEN) */}
        <Card className="dark:bg-slate-900 dark:border-gray-700 overflow-hidden">
          
          {/* Messaging Header */}
          <CardHeader className="bg-green-600 text-white p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <MessageSquare className="w-6 h-6 mr-2" />
                <CardTitle className="text-lg font-semibold">Messaging</CardTitle>
              </div>
              
              {/* Service Switcher */}
              <div className="flex space-x-2">
                <Button
                  onClick={() => setSelectedMessagingService('whatsapp-private')}
                  size="sm"
                  className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                    selectedMessagingService === 'whatsapp-private'
                      ? 'bg-white text-green-600'
                      : 'bg-green-700 text-white hover:bg-green-800'
                  }`}
                >
                  WhatsApp Privat
                </Button>
                <Button
                  onClick={() => setSelectedMessagingService('whatsapp-business')}
                  size="sm"
                  className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                    selectedMessagingService === 'whatsapp-business'
                      ? 'bg-white text-green-600'
                      : 'bg-green-700 text-white hover:bg-green-800'
                  }`}
                >
                  WhatsApp Business
                </Button>
              </div>
            </div>
          </CardHeader>

          {/* Messaging Content */}
          <CardContent className="p-0">
            <div className="flex h-96">
              
              {/* Contacts Sidebar */}
              <div className="w-1/3 border-r border-gray-200 dark:border-gray-700 p-4 overflow-y-auto">
                <h4 className="font-semibold mb-4 text-gray-900 dark:text-white">Kontakte</h4>
                
                <div className="space-y-2">
                  {[
                    { name: 'Test Handy 1', phone: '+49xxx1', status: 'online', lastMessage: 'Hallo!' },
                    { name: 'Test Handy 2', phone: '+49xxx2', status: 'offline', lastMessage: 'Bis später' },
                    { name: 'Geschäfts-Kontakt', phone: '+49xxx3', status: 'online', lastMessage: 'Meeting um 15:00' },
                    { name: 'Familie', phone: '+49xxx4', status: 'online', lastMessage: 'Alles klar?' }
                  ].map((contact, index) => (
                    <Button
                      key={index}
                      onClick={() => setSelectedContact(contact)}
                      variant="ghost"
                      className={`w-full text-left p-3 rounded-lg transition-colors h-auto justify-start ${
                        selectedContact?.phone === contact.phone
                          ? 'bg-green-100 dark:bg-green-900'
                          : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      <div className="flex justify-between items-start w-full">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900 dark:text-white">{contact.name}</div>
                          <div className="text-sm text-gray-500 truncate">{contact.lastMessage}</div>
                        </div>
                        <div className={`w-2 h-2 rounded-full mt-2 ${
                          contact.status === 'online' ? 'bg-green-500' : 'bg-gray-400'
                        }`}></div>
                      </div>
                    </Button>
                  ))}
                </div>
              </div>

              {/* Chat Area */}
              <div className="flex-1 flex flex-col">
                {selectedContact ? (
                  <>
                    {/* Chat Header */}
                    <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-slate-800">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold text-gray-900 dark:text-white">{selectedContact.name}</h4>
                          <p className="text-sm text-gray-500">{selectedContact.phone}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${
                            selectedContact.status === 'online' ? 'bg-green-500' : 'bg-gray-400'
                          }`}></div>
                          <span className="text-sm text-gray-500 capitalize">{selectedContact.status}</span>
                        </div>
                        <div className="ml-auto flex gap-2">
                          {/* Favorites Star */}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleToggleFavorite(selectedTelegramChat.id, !selectedTelegramChat.is_favorite)}
                            className="h-8 w-8 p-0"
                          >
                            <Crown className={`w-4 h-4 ${selectedTelegramChat.is_favorite ? 'text-yellow-500 fill-yellow-500' : 'text-gray-400'}`} />
                          </Button>
                          
                          {/* Delete Button */}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteChat(selectedTelegramChat.id)}
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>

                      </div>
                    </div>

                    {/* Chat Messages */}
                    <div className="flex-1 p-4 overflow-y-auto bg-gray-50 dark:bg-slate-900">
                      <div className="space-y-3">
                        {chatHistory.map((msg, index) => (
                          <div key={index} className={`flex ${msg.type === 'sent' ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-xs px-4 py-2 rounded-lg ${
                              msg.type === 'sent' 
                                ? 'bg-green-600 text-white' 
                                : 'bg-white dark:bg-slate-800 text-gray-900 dark:text-white'
                            }`}>
                              <p className="text-sm">{msg.message}</p>
                              <p className="text-xs opacity-70 mt-1">{msg.timestamp}</p>
                            </div>
                          </div>
                        ))}
                        
                        {/* Sample messages */}
                        <div className="flex justify-start">
                          <div className="max-w-xs px-4 py-2 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white">
                            <p className="text-sm">{selectedContact.lastMessage}</p>
                            <p className="text-xs opacity-70 mt-1">vor 5 Min</p>
                          </div>
                        </div>
                        <div className="ml-auto flex gap-2">
                          {/* Favorites Star */}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleToggleFavorite(selectedTelegramChat.id, !selectedTelegramChat.is_favorite)}
                            className="h-8 w-8 p-0"
                          >
                            <Crown className={`w-4 h-4 ${selectedTelegramChat.is_favorite ? 'text-yellow-500 fill-yellow-500' : 'text-gray-400'}`} />
                          </Button>
                          
                          {/* Delete Button */}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteChat(selectedTelegramChat.id)}
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>

                      </div>
                    </div>

                    {/* Message Input */}
                    <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-900">
                      <div className="flex gap-2">
                        <Input
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          placeholder="Nachricht eingeben..."
                          className="flex-1"
                          onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                        />
                        <Button 
                          onClick={sendMessage}
                          disabled={!message.trim()}
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center bg-gray-50 dark:bg-slate-900">
                    <div className="text-center">
                      <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500 dark:text-gray-400">Wählen Sie einen Kontakt aus, um zu chatten</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* 6. CHAT ROOMS (EXISTING) */}
        <Card className="dark:bg-slate-900 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 dark:text-white">
              <MessageSquare className="w-5 h-5 text-purple-600" />
              Chat-Räume
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {chatRooms.map((room) => (
                <div 
                  key={room.id} 
                  className="flex items-center justify-between p-4 bg-gray-50 dark:bg-slate-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer transition-colors"
                  onClick={() => openChatRoom(room)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center">
                      <MessageSquare className="w-6 h-6 text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium dark:text-white">{room.name}</p>
                        {room.encrypted && (
                          <div className="flex items-center gap-1 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 px-2 py-1 rounded text-xs">
                            <CheckCircle className="w-3 h-3" />
                            <span>Verschlüsselt</span>
                          </div>
                        )}
                        {room.unread > 0 && (
                          <Badge className="bg-red-500 text-white">
                            {room.unread}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        {room.lastMessage}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {room.members} Mitglieder • {formatTime(room.lastActivity)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                      <Users className="w-4 h-4" />
                      <span className="text-sm">{room.members}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* 7. COMING SOON FEATURES (EXISTING) */}
        <Card className="dark:bg-slate-900 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 dark:text-white">
              <Settings className="w-5 h-5 text-gray-600" />
              Bald verfügbar
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 dark:bg-slate-800 rounded-lg">
                <h4 className="font-medium mb-2 dark:text-white">Datei-Sharing</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">Sichere Übertragung von Dokumenten und Bildern</p>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-slate-800 rounded-lg">
                <h4 className="font-medium mb-2 dark:text-white">Voice Messages</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">Sprachnachrichten für schnelle Kommunikation</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Decode email subject from MIME encoding
  const decodeEmailSubject = (encodedSubject) => {
    if (!encodedSubject) return 'Kein Betreff';
    
    // Handle MIME encoded subjects like =?utf-8?B?...?=
    if (encodedSubject.includes('=?') && encodedSubject.includes('?=')) {
      try {
        const matches = encodedSubject.match(/=\?([^?]+)\?([BQ])\?([^?]+)\?=/g);
        if (matches) {
          let decoded = encodedSubject;
          matches.forEach(match => {
            const parts = match.match(/=\?([^?]+)\?([BQ])\?([^?]+)\?=/);
            if (parts) {
              const [, charset, encoding, data] = parts;
              if (encoding === 'B') {
                // Base64 decoding
                try {
                  const decodedData = atob(data);
                  decoded = decoded.replace(match, decodedData);
                } catch (e) {
                  console.warn('Base64 decode error:', e);
                }
              } else if (encoding === 'Q') {
                // Quoted-printable decoding (simplified)
                const decodedData = data.replace(/=([0-9A-F]{2})/g, (_, hex) => 
                  String.fromCharCode(parseInt(hex, 16))
                ).replace(/_/g, ' ');
                decoded = decoded.replace(match, decodedData);
              }
            }
          });
          return decoded;
        }
      } catch (e) {
        console.warn('Subject decode error:', e);
      }
    }
    
    return encodedSubject;
  };

  const renderClaraMailContent = () => {
    return (
      <div className="space-y-6">
        {/* Hero Section */}
        <Card className="dark:bg-slate-900 dark:border-gray-700 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-500 to-indigo-600 dark:from-blue-600 dark:to-indigo-700 text-white p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold mb-2">Clara Mail</h2>
                <p className="text-blue-100">Ihre E-Mails sicher und übersichtlich - powered by NextCloud</p>
              </div>
              <div className="flex items-center gap-4">
                <Button 
                  onClick={() => window.open("https://hub.clara360.de/apps/mail", "_blank")}
                  className="bg-white text-blue-600 hover:bg-blue-50"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Neuer Tab
                </Button>
                <div className="flex items-center gap-2 bg-white bg-opacity-20 px-3 py-1 rounded-full">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <span className="text-sm">Online</span>
                </div>
              </div>
            </div>
          </div>
        </Card>


        {/* Mail Intelligence Card */}
        <Card className="dark:bg-slate-900 dark:border-gray-700 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-500 to-indigo-600 dark:from-blue-600 dark:to-indigo-700 text-white p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Brain className="w-6 h-6" />
                <div>
                  <h3 className="text-lg font-semibold">Mail Intelligence</h3>
                  <p className="text-blue-100 text-sm">KI-gestützte E-Mail-Analyse</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  onClick={fetchEmails}
                  disabled={emailsLoading}
                  className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white border-white border-opacity-30"
                  size="sm"
                >
                  <Clock className="w-4 h-4 mr-1" />
                  {emailsLoading ? 'Laden...' : 'Aktualisieren'}
                </Button>
                <Button 
                  onClick={() => window.open('https://mail.clara360.de', '_blank')}
                  className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white border-white border-opacity-30"
                  size="sm"
                >
                  <ExternalLink className="w-4 h-4 mr-1" />
                  Roundcube
                </Button>
              </div>
            </div>
          </div>
          <CardContent className="p-6">
            {emailsError && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
                <p className="text-red-600 dark:text-red-400 text-sm">
                  Fehler beim Laden der E-Mails: {emailsError}
                </p>
              </div>
            )}
            
            {emailsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600 dark:text-gray-400">E-Mails werden geladen...</span>
              </div>
            ) : emails.length === 0 ? (
              <div className="text-center py-8">
                <MailOpen className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 dark:text-gray-400">Keine E-Mails gefunden</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold text-gray-900 dark:text-white">
                    Neueste E-Mails ({emails.length})
                  </h4>
                </div>
                  <Button 
                    onClick={() => fetchEmails()}
                    size="sm"
                    variant="outline"
                    className="text-blue-600 border-blue-600 hover:bg-blue-50"
                  >
                    <RefreshCw className="w-4 h-4 mr-1" />
                    Mehr laden
                  </Button>
                
                <div className="max-h-[600px] overflow-y-auto space-y-4">
                {emails.map((email, index) => (
                  <div key={email.message_id || index} id={`email-${index}`} className="bg-gray-50 dark:bg-slate-800 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <h5 className="font-medium text-gray-900 dark:text-white truncate">
                          {decodeEmailSubject(email?.subject || "")}
                        </h5>
                        <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                          Von: {email.from?.replace(/[<>]/g, '')}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-500">
                          {email.date ? new Date(email.date).toLocaleString('de-DE') : 'Unbekanntes Datum'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        {email.has_attachments && (
                          <FileText className="w-4 h-4 text-gray-400" />
                        )}
                        <Button 
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                          onClick={() => handleEmailSummarize(email)}
                        >
                          <Sparkles className="w-3 h-3 mr-1" />
                          Zusammenfassen
                        </Button>
                      </div>
                    </div>
                    
                    {email.snippet && (
                      <div className="bg-white dark:bg-slate-900 rounded p-3 border border-gray-200 dark:border-gray-600">
                        <div className="flex items-start justify-between">
                          <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2 flex-1">
                            {email.snippet}
                          </p>
                          {email.snippet && (
                            <button
                              onClick={() => {
                                const emailElement = document.getElementById(`email-${index}`);
                                const fullContent = emailElement.querySelector('.full-email-content');
                                const expandIcon = emailElement.querySelector('.expand-icon');
                                
                                if (fullContent.classList.contains('hidden')) {
                                  fullContent.classList.remove('hidden');
                                  expandIcon.style.transform = 'rotate(180deg)';
                                } else {
                                  fullContent.classList.add('hidden');
                                  expandIcon.style.transform = 'rotate(0deg)';
                                }
                              }}
                              className="ml-2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                              title="Vollständige E-Mail anzeigen"
                            >
                              <ChevronDown className="w-4 h-4 expand-icon transition-transform duration-200" />
                            </button>
                          )}
                        </div>
                        
                        {/* Full email content - hidden by default */}
                        <div className="full-email-content hidden mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                          <div className="max-h-[600px] overflow-y-auto bg-gray-50 dark:bg-slate-800 rounded p-3 border">
                            <pre className="text-xs text-gray-600 dark:text-gray-400 whitespace-pre-wrap font-mono leading-relaxed">
                              {email.full_content || email.content || email.snippet}
                            </pre>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                            Vollständige E-Mail ({(email.full_content || email.content || email.snippet).length} Zeichen)
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        {/* Ansichtsoptionen entfernt - mehr Platz für Mail-Features */}


        {/* Features */}
        {!useIframe && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="dark:bg-slate-900 dark:border-gray-700">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-blue-600" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Sicher & Privat</h4>
                <p className="text-gray-600 dark:text-gray-300">
                  Ihre E-Mails werden verschlüsselt übertragen und gespeichert.
                </p>
              </CardContent>
            </Card>
            
            <Card className="dark:bg-slate-900 dark:border-gray-700">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center mb-4">
                  <Server className="w-6 h-6 text-green-600" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Eigene Infrastruktur</h4>
                <p className="text-gray-600 dark:text-gray-300">
                  Volle Kontrolle über Ihre Daten mit selbst-gehosteter Lösung.
                </p>
              </CardContent>
            </Card>
            
            <Card className="dark:bg-slate-900 dark:border-gray-700">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center mb-4">
                  <Brain className="w-6 h-6 text-purple-600" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">KI-Integration</h4>
                <p className="text-gray-600 dark:text-gray-300">
                  Intelligente E-Mail-Analyse und automatische Kategorisierung.
                </p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    );
  };

  const renderWhatsAppContent = () => {
    return (
      <div className="space-y-6">
        <Card className="dark:bg-slate-900 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 dark:text-white">
              <MessageSquare className="w-5 h-5 text-green-600" />
              WhatsApp Integration
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                WhatsApp Integration
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Verbinden Sie Ihr WhatsApp-Konto für nahtlose Kommunikation
              </p>
              <Button 
                onClick={loadWhatsAppContacts}
                disabled={whatsappLoading}
                className="bg-green-600 hover:bg-green-700"
              >
                {whatsappLoading ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Verbinde...
                  </>
                ) : (
                  <>
                    <MessageSquare className="w-4 h-4 mr-2" />
                    WhatsApp verbinden
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderCalendarContent = () => {
    return (
      <div className="space-y-6">
        <Card className="dark:bg-slate-900 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 dark:text-white">
              <Calendar className="w-5 h-5 text-blue-600" />
              Kalender
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ClaraCalendar />
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderAnnouncementsContent = () => {
    return (
      <div className="space-y-6">
        <Card className="dark:bg-slate-900 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 dark:text-white">
              <Megaphone className="w-5 h-5 text-orange-600" />
              Ankündigungen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <Megaphone className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Keine Ankündigungen
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Hier werden wichtige Mitteilungen angezeigt
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const tabs = [
    {
      id: 'clara-video',
      label: 'Clara Video',
      icon: Video,
      content: renderClaraVideoContent
    },
    {
      id: 'clara-chat',
      label: 'Clara Chat',
      icon: MessageSquare,
      content: renderClaraChatContent
    },
    {
      id: 'clara-mail',
      label: 'Clara Mail',
      icon: Mail,
      content: () => <ClaraMailAI />
    },
    {
      id: 'whatsapp',
      label: 'WhatsApp',
      icon: MessageSquare,
      content: renderWhatsAppContent
    },
    {
      id: 'calendar',
      label: 'Kalender',
      icon: Calendar,
      content: renderCalendarContent
    },
    {
      id: 'announcements',
      label: 'Ankündigungen',
      icon: Megaphone,
      content: renderAnnouncementsContent
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-black p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Kommunikation
          </h1>
          
          {/* Clara Hub Button with Coming Soon annotation */}
          <div className="relative">
            {/* Coming Soon annotation */}
            <div className="absolute -top-8 -right-4 transform rotate-12 pointer-events-none">
              <div className="relative">
                <span className="text-sm font-handwriting text-purple-600 dark:text-purple-400 whitespace-nowrap">
                  Coming Soon
                </span>
                {/* Arrow pointing to button */}
                <svg 
                  className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-6 h-6 text-purple-600 dark:text-purple-400" 
                  fill="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
              </div>
            </div>
            
            {/* Clara Hub Button */}
            <button
              className="relative group bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white px-6 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 ease-in-out"
              onClick={() => {
                // Open Clara Hub (NextCloud) in new tab
                window.open('https://hub.clara360.de', '_blank');
              }}
            >
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                <span>Clara Hub</span>
              </div>
              
              {/* Hover effect */}
              <div className="absolute inset-0 rounded-full bg-white opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
            </button>
          </div>
          <p className="text-gray-600 dark:text-gray-300">
            Verwalten Sie alle Kommunikationskanäle zentral
          </p>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="flex flex-wrap gap-2 bg-white dark:bg-slate-900 p-2 rounded-lg shadow-sm border dark:border-gray-700">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors ${
                    activeTab === tab.id
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="font-medium">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border dark:border-gray-700 p-6">
          {tabs.find(tab => tab.id === activeTab)?.content()}
        </div>

        {/* Modals */}
        {showVideoModal && selectedMeeting && (
          <ClaraVideoModal
            isOpen={showVideoModal}
            onClose={() => setShowVideoModal(false)}
            meeting={selectedMeeting}
          />
        )}

        {showChatModal && (
          <ClaraChatModal
            isOpen={showChatModal}
            onClose={() => setShowChatModal(false)}
            selectedRoom={selectedRoom}
          />
        )}

        {/* Telegram Connection Modal - FIXED VERSION */}
        {showTelegramConnect && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-slate-900 rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold dark:text-white">Telegram verbinden</h3>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => {
                    setShowTelegramConnect(false);
                    setTelegramStatus('idle');
                    setTelegramQrCode(null);
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="space-y-4">
                {telegramStatus === 'idle' && (
                  <>
                    <div className="text-center">
                      <div className="w-32 h-32 bg-gray-100 dark:bg-slate-800 rounded-lg mx-auto mb-4 flex items-center justify-center">
                        <MessageSquare className="w-16 h-16 text-blue-600" />
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        Verbinde dein Telegram-Konto mit Clara360
                      </p>
                    </div>
                    
                    <div className="space-y-3">
                      <Button 
                        onClick={() => handleTelegramConnect("qr")}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        📷 QR-Code scannen
                      </Button>
                      
                      <Button 
                        onClick={() => handleTelegramConnect("phone")}
                        variant="outline"
                        className="w-full"
                      >
                        📞 Telefonnummer eingeben
                      </Button>
                    </div>
                  </>
                )}

                {telegramStatus === 'qr_pending' && telegramQrCode && (
                  <div className="text-center">
                    <div className="mb-4">
                      <img 
                        src={telegramQrCode} 
                        alt="Telegram QR Code" 
                        className="mx-auto w-48 h-48 border rounded-lg"
                      />
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      Scannen Sie diesen QR-Code mit Ihrer Telegram App
                    </p>
                    <div className="flex items-center justify-center gap-2 text-blue-600">
                      <Loader className="w-4 h-4 animate-spin" />
                      <span className="text-sm">Warten auf Scan...</span>
                    </div>
                  </div>
                )}

                {(telegramStatus === 'phone_pending' || telegramStatus === 'qr_pending') && (
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-2 text-blue-600 mb-4">
                      <Loader className="w-4 h-4 animate-spin" />
                      <span className="text-sm">Verbindung wird hergestellt...</span>
                    </div>
                  </div>
                )}
                
                <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
                  Deine Telegram-Daten bleiben sicher und werden nur für die Chat-Funktionalität verwendet.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Clara Chat Room */}
        {selectedChatRoom && (
          <ClaraChatRoom
            room={selectedChatRoom}
            onClose={() => setSelectedChatRoom(null)}
          />
        )}

        {/* Telegram Chat Modal */}
        {showTelegramChat && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-slate-900 rounded-lg w-full max-w-6xl h-[85vh] mx-4 flex">
              {/* Chat List Sidebar */}
              <div className="w-1/3 border-r dark:border-gray-700 flex flex-col">
                <div className="p-4 border-b dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold dark:text-white">Telegram Chats</h3>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => {
                        setShowTelegramChat(false);
                        setSelectedTelegramChat(null);
                        setTelegramMessages([]);
                      }}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {/* Chat Management Controls */}
                  <div className="mt-3 space-y-3">
                    {/* Sort & Filter Row */}
                    <div className="flex gap-2">
                      <Select defaultValue="activity">
                        <SelectTrigger className="flex-1 h-8">
                          <SelectValue placeholder="Sortieren" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="activity">Nach Aktivität</SelectItem>
                          <SelectItem value="favorites">Favoriten zuerst</SelectItem>
                          <SelectItem value="alphabetical">Alphabetisch</SelectItem>
                          <SelectItem value="unread">Ungelesen zuerst</SelectItem>
                        </SelectContent>
                      </Select>
                      
                      <Select defaultValue="all">
                        <SelectTrigger className="flex-1 h-8">
                          <SelectValue placeholder="Filter" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Alle Chats</SelectItem>
                          <SelectItem value="favorites">⭐ Favoriten</SelectItem>
                          <SelectItem value="active">🟢 Aktiv (30 Tage)</SelectItem>
                          <SelectItem value="private">👤 Privat</SelectItem>
                          <SelectItem value="groups">👥 Gruppen</SelectItem>
                          <SelectItem value="channels">📢 Channels</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Quick Action Buttons */}
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" className="h-7 px-2 text-xs">
                        <Download className="w-3 h-3 mr-1" />
                        2M Import
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 px-2 text-xs">
                        <Filter className="w-3 h-3 mr-1" />
                        Aufräumen
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 px-2 text-xs">
                        <Search className="w-3 h-3 mr-1" />
                        Suchen
                      </Button>
                    </div>
                  </div>
                </div>
                
                <div className="flex-1 overflow-y-auto">
                  {telegramChatLoading ? (
                    <div className="flex items-center justify-center p-8">
                      <Loader className="w-6 h-6 animate-spin mr-2" />
                      <span className="text-sm dark:text-gray-400">Lade Chats...</span>
                    </div>
                  ) : telegramChats.length === 0 ? (
                    <div className="text-center p-8">
                      <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-500 dark:text-gray-400">Keine Chats gefunden</p>
                      <Button 
                        onClick={() => fetchTelegramChats()}
                        className="mt-4 bg-blue-600 hover:bg-blue-700 text-white"
                        size="sm"
                      >
                        Chats laden
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-1 p-2">
                      {telegramChats.map((chat) => (
                        <div key={chat.id} className="relative group">
                          <button
                            onClick={() => setSelectedTelegramChat(chat)}
                            className={`w-full text-left p-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                              selectedTelegramChat?.id === chat.id ? 'bg-blue-100 dark:bg-blue-900' : ''
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold">
                                {chat.name.charAt(0).toUpperCase()}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <h4 className="font-medium text-sm dark:text-white truncate">
                                      {chat.name}
                                    </h4>
                                    {/* Favorite Star */}
                                    <button
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        try {
                                          const response = await fetch(`${TELEGRAM_API_BASE}/chat/favorite`, {
                                            method: 'POST',
                                            headers: { 'Content-Type': 'application/json' },
                                            body: JSON.stringify({
                                              user_id: telegramUser.id,
                                              chat_id: chat.id,
                                              is_favorite: !chat.is_favorite
                                            })
                                          });
                                          
                                          if (response.ok) {
                                            // Update local state
                                            setTelegramChats(prev => 
                                              prev.map(c => 
                                                c.id === chat.id 
                                                  ? { ...c, is_favorite: !c.is_favorite }
                                                  : c
                                              )
                                            );
                                          }
                                        } catch (error) {
                                          console.error('Error toggling favorite:', error);
                                        }
                                      }}
                                      className={`opacity-0 group-hover:opacity-100 transition-opacity hover:text-yellow-500 ${
                                        chat.is_favorite ? 'text-yellow-500 opacity-100' : ''
                                      }`}
                                    >
                                      ⭐
                                    </button>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    {chat.unread_count > 0 && (
                                      <Badge className="bg-blue-600 text-white text-xs">
                                        {chat.unread_count}
                                      </Badge>
                                    )}
                                    {/* Chat Type Icon */}
                                    <span className="text-xs">
                                      {chat.type === 'private' ? '👤' : 
                                       chat.type === 'group' ? '👥' : 
                                       chat.type === 'channel' ? '📢' : '💬'}
                                    </span>
                                  </div>
                                </div>
                                <div className="flex items-center justify-between">
                                  <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                                    {chat.type}
                                  </p>
                                  {/* Last Activity */}
                                  <span className="text-xs text-gray-400">
                                    {chat.last_message_date ? 
                                      new Date(chat.last_message_date).toLocaleDateString('de-DE', {
                                        day: '2-digit',
                                        month: '2-digit'
                                      }) : 
                                      'Neu'
                                    }
                                  </span>
                                </div>
                              </div>
                            </div>
                          </button>
                          
                          {/* Quick Actions (visible on hover) */}
                          <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 w-6 p-0 hover:bg-red-100 hover:text-red-600"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (confirm(`Chat "${chat.name}" löschen?`)) {
                                    // TODO: Delete chat
                                    console.log('Delete chat', chat.id);
                                  }
                                }}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Chat Messages Area */}
              <div className="flex-1 flex flex-col">
                {selectedTelegramChat ? (
                  <>
                    {/* Chat Header */}
                    <div className="p-4 border-b dark:border-gray-700">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold">
                          {selectedTelegramChat.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <h3 className="font-semibold dark:text-white">{selectedTelegramChat.name}</h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400 capitalize">
                            {selectedTelegramChat.type}
                            {selectedTelegramChat.participants_count > 0 && 
                              ` • ${selectedTelegramChat.participants_count} Mitglieder`
                            }
                          </p>
                        </div>
                        <div className="ml-auto flex gap-2">
                          {/* Favorites Star */}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleToggleFavorite(selectedTelegramChat.id, !selectedTelegramChat.is_favorite)}
                            className="h-8 w-8 p-0"
                          >
                            <Crown className={`w-4 h-4 ${selectedTelegramChat.is_favorite ? 'text-yellow-500 fill-yellow-500' : 'text-gray-400'}`} />
                          </Button>
                          
                          {/* Delete Button */}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteChat(selectedTelegramChat.id)}
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>

                      </div>
                    </div>

                    {/* Messages Area */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                      {(!telegramMessages[selectedTelegramChat?.id] || telegramMessages[selectedTelegramChat?.id].length === 0) ? (
                        <div className="text-center text-gray-500 dark:text-gray-400 mt-8">
                          <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
                          <p>Keine Nachrichten vorhanden</p>
                          <p className="text-sm">Sende eine Nachricht um zu beginnen</p>
                        </div>
                      ) : (
                        (telegramMessages[selectedTelegramChat?.id] || []).map((message, index) => (
                          <div
                            key={message.id || index}
                            className={`flex ${message.is_from_me ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg relative ${
                                message.is_from_me
                                  ? message.status === 'error' 
                                    ? 'bg-red-500 text-white'
                                    : 'bg-blue-600 text-white'
                                  : 'bg-gray-200 dark:bg-slate-800 text-gray-900 dark:text-white'
                              }`}
                            >
                              {!message.is_from_me && (
                                <p className="text-xs font-semibold mb-1 opacity-75">
                                  {message.sender_name}
                                </p>
                              )}
                              
                              <p className="text-sm">{renderMessageContent(message.content)}</p>
                              
                              {/* Error message */}
                              {message.status === 'error' && message.error && (
                                <p className="text-xs mt-1 text-red-100 bg-red-600 bg-opacity-50 px-2 py-1 rounded">
                                  {message.error}
                                </p>
                              )}
                              
                              <div className="flex items-center justify-between mt-1">
                                <p className={`text-xs ${
                                  message.is_from_me ? 'text-blue-100' : 'text-gray-500 dark:text-gray-400'
                                }`}>
                                  {new Date(message.timestamp).toLocaleTimeString()}
                                </p>
                                
                                {/* Status indicators for own messages */}
                                {message.is_from_me && (
                                  <div className="flex items-center ml-2">
                                    {message.status === 'sending' && (
                                      <Loader className="w-3 h-3 animate-spin text-blue-200" />
                                    )}
                                    {message.status === 'sent' && (
                                      <Check className="w-3 h-3 text-blue-200" />
                                    )}
                                    {message.status === 'error' && (
                                      <button
                                        onClick={() => retryMessage(message.id)}
                                        className="flex items-center text-red-200 hover:text-white transition-colors"
                                        title="Erneut versuchen"
                                      >
                                        <RotateCcw className="w-3 h-3" />
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    {/* Message Input */}
                    <div className="p-4 border-t dark:border-gray-700">
                      <div className="flex gap-2">
                        <Input
                          value={newTelegramMessage}
                          onChange={(e) => setNewTelegramMessage(e.target.value)}
                          placeholder="Nachricht eingeben..."
                          className="flex-1"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              sendTelegramMessage();
                            }
                          }}
                        />
                        <Button
                          onClick={sendTelegramMessage}
                          disabled={!newTelegramMessage.trim()}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-center text-gray-500 dark:text-gray-400">
                      <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-50" />
                      <h3 className="text-lg font-semibold mb-2">Wähle einen Chat</h3>
                      <p>Wähle einen Chat aus der Liste um zu beginnen</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Email Summary Modal */}
        <EmailSummaryModal
          isOpen={showEmailSummary}
          onClose={() => setShowEmailSummary(false)}
          email={selectedEmail}
        />
      </div>
    </div>
  );
};

export default CommunicationPage;
